import React, { useEffect, useState } from 'react';
import { Link } from 'react-router-dom';
import { ArrowRight, Calendar, User } from 'lucide-react';
import { Card, CardContent } from '../components/ui/card';
import BlogImage from '../components/BlogImage';
import axios from 'axios';
import { toast } from 'sonner';

const API_URL = `${process.env.REACT_APP_BACKEND_URL}/api`;

const Blog = () => {
  const [blogPosts, setBlogPosts] = useState([]);
  const [loading, setLoading] = useState(true);
  const [email, setEmail] = useState('');
  const [subName, setSubName] = useState('');
  const [subscribing, setSubscribing] = useState(false);
  const [pageContent, setPageContent] = useState(null);

  useEffect(() => {
    fetchBlogPosts();
  }, []);

  const fetchBlogPosts = async () => {
    try {
      const [postsRes, pageRes] = await Promise.all([
        axios.get(`${API_URL}/blog/posts`, { params: { published: true } }),
        axios.get(`${API_URL}/page-content/blog`)
      ]);
      setBlogPosts(postsRes.data);
      setPageContent(pageRes.data);
    } catch (error) {
      console.error('Error fetching blog posts:', error);
    } finally {
      setLoading(false);
    }
  };

  const handleSubscribe = async (e) => {
    e.preventDefault();
    if (!email) {
      toast.error('Please enter your email');
      return;
    }

    setSubscribing(true);
    try {
      await axios.post(`${API_URL}/newsletter/subscribe`, { name: subName, email, source: 'blog' });
      toast.success('Thank you for subscribing!');
      setEmail('');
      setSubName('');
    } catch (error) {
      console.error('Error subscribing:', error);
      toast.error('Failed to subscribe. Please try again.');
    } finally {
      setSubscribing(false);
    }
  };

  if (loading) {
    return (
      <div className="min-h-screen flex items-center justify-center">
        <div className="text-2xl text-gray-600">Loading...</div>
      </div>
    );
  }

  return (
    <div className="min-h-screen">
      {/* Hero Section */}
      <section className="py-20 bg-gradient-to-br from-sage-50 via-lavender-50 to-cream-50">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="text-center space-y-6">
            <h1 className="text-5xl md:text-6xl font-serif text-gray-900">{pageContent?.title || 'Blog'}</h1>
            <p className="text-xl text-gray-600 max-w-3xl mx-auto">
              {pageContent?.description || 'Stories, insights, and guidance for your healing journey'}
            </p>
          </div>
        </div>
      </section>

      {/* Blog Posts Grid */}
      <section className="py-20 bg-white">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="grid md:grid-cols-2 lg:grid-cols-3 gap-8">
            {blogPosts.map((post) => (
              <Card
                key={post.id}
                className="border-0 shadow-lg hover:shadow-xl transition-all duration-300 hover:-translate-y-2 overflow-hidden bg-white group"
              >
                <div className="aspect-[16/9] overflow-hidden">
                  <BlogImage src={post.image} position={post.imagePosition || post.image_position} alt={post.title} />
                </div>
                <CardContent className="p-6 space-y-4">
                  <div className="flex items-center justify-between text-sm text-gray-500">
                    <div className="flex items-center space-x-2">
                      <Calendar className="h-4 w-4" />
                      <span>{new Date(post.date).toLocaleDateString()}</span>
                    </div>
                    <span className="bg-sage-100 text-sage-800 px-3 py-1 rounded-full text-xs font-medium">
                      {post.category}
                    </span>
                  </div>
                  <h3 className="text-xl font-semibold text-gray-900 line-clamp-2">{post.title}</h3>
                  <p className="text-gray-600 line-clamp-3 leading-relaxed">{post.excerpt}</p>
                  <div className="flex items-center justify-between pt-4">
                    <div className="flex items-center space-x-2 text-sm text-gray-600">
                      <User className="h-4 w-4" />
                      <span>{post.author}</span>
                    </div>
                    <Link
                      to={`/blog/${post.id}`}
                      className="inline-flex items-center text-sage-700 font-medium hover:text-sage-800 transition-colors"
                    >
                      Read More
                      <ArrowRight className="ml-2 h-4 w-4" />
                    </Link>
                  </div>
                </CardContent>
              </Card>
            ))}
          </div>
        </div>
      </section>

      {/* Newsletter CTA */}
      <section className="py-20 bg-sage-50">
        <div className="max-w-3xl mx-auto px-4 sm:px-6 lg:px-8">
          <Card className="border-0 shadow-xl bg-white">
            <CardContent className="p-12 text-center space-y-6">
              <h2 className="text-3xl md:text-4xl font-serif text-gray-900">
                Never Miss an Update
              </h2>
              <p className="text-lg text-gray-600">
                Subscribe to get the latest blog posts and healing resources delivered to your inbox
              </p>
              <form onSubmit={handleSubscribe} className="flex flex-col gap-4 max-w-xl mx-auto">
                <input
                  type="text"
                  value={subName}
                  onChange={(e) => setSubName(e.target.value)}
                  placeholder="Your name"
                  data-testid="blog-subscribe-name"
                  className="w-full px-6 py-4 rounded-xl border-2 border-sage-200 focus:border-sage-500 focus:outline-none transition-colors"
                />
                <div className="flex flex-col sm:flex-row gap-4">
                  <input
                    type="email"
                    value={email}
                    onChange={(e) => setEmail(e.target.value)}
                    placeholder="Enter your email"
                    required
                    data-testid="blog-subscribe-email"
                    className="flex-1 px-6 py-4 rounded-xl border-2 border-sage-200 focus:border-sage-500 focus:outline-none transition-colors"
                  />
                  <button
                    type="submit"
                    disabled={subscribing}
                    data-testid="blog-subscribe-btn"
                    className="bg-sage-700 hover:bg-sage-800 text-white px-8 py-4 rounded-xl transition-all duration-300 font-medium"
                  >
                    {subscribing ? 'Subscribing...' : 'Subscribe'}
                  </button>
                </div>
              </form>
            </CardContent>
          </Card>
        </div>
      </section>
    </div>
  );
};

export default Blog;